/**
 * POS Bill to Bill Report Screen
 * Detailed transaction report with Report Engine integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Calendar, Search } from 'lucide-react';
import { ReportLayout } from '@/components/report-engine';
import api from '@/services/api';

const POSBillToBillReport = ({ theme }) => {
  const isDark = theme === 'dark';
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [fromDate, setFromDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [billNoSearch, setBillNoSearch] = useState('');
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchBillData();
  }, [fromDate, toDate, page, pageSize, billNoSearch]);

  const fetchBillData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/report-engine/data/POS_BILL_TO_BILL', {
        page,
        pageSize,
        filters: { fromDate, toDate, billNo: billNoSearch || undefined }
      });

      if (response.data.success) {
        setData(response.data.data?.rows || []);
        setTotalRows(response.data.data?.total || 0);
        setTotalPages(response.data.data?.totalPages || 1);
      } else {
        setData(generateMockData());
      }
    } catch (err) {
      console.error('Error fetching bill data:', err);
      setData(generateMockData());
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    return Array.from({ length: 30 }, (_, i) => ({
      SlNo: i + 1,
      BillNo: `POS/2024/${String(1000 + i).padStart(4, '0')}`,
      BillDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      BillTime: `${String(Math.floor(Math.random() * 12) + 8).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
      Counter: `Counter ${Math.floor(Math.random() * 3) + 1}`,
      Salesman: ['Rajesh', 'Priya', 'Amit'][Math.floor(Math.random() * 3)],
      CustomerName: `Customer ${i + 1}`,
      CustomerPhone: `98765${String(Math.floor(Math.random() * 100000)).padStart(5, '0')}`,
      TotalItems: Math.floor(Math.random() * 10) + 1,
      TotalQty: Math.floor(Math.random() * 20) + 1,
      GrossAmount: (Math.random() * 10000 + 500).toFixed(2),
      ItemDiscount: (Math.random() * 200).toFixed(2),
      BillDiscount: (Math.random() * 100).toFixed(2),
      TaxAmount: (Math.random() * 300).toFixed(2),
      RoundOff: (Math.random() * 2 - 1).toFixed(2),
      NetAmount: 0,
      CashAmount: 0,
      CardAmount: 0,
      UPIAmount: 0,
      Status: Math.random() > 0.1 ? 'Completed' : 'Cancelled'
    })).map(row => {
      const net = parseFloat(row.GrossAmount) - parseFloat(row.ItemDiscount) - parseFloat(row.BillDiscount) + parseFloat(row.TaxAmount) + parseFloat(row.RoundOff);
      return {
        ...row,
        NetAmount: net.toFixed(2),
        CashAmount: (net * 0.6).toFixed(2),
        CardAmount: (net * 0.3).toFixed(2),
        UPIAmount: (net * 0.1).toFixed(2)
      };
    });
  };

  const handleRefresh = useCallback(() => {
    fetchBillData();
  }, [fromDate, toDate, page, pageSize, billNoSearch]);

  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value || 0);

  const columns = [
    { key: 'SlNo', label: 'Sl.No', width: 50, align: 'center' },
    { key: 'BillNo', label: 'Bill No', width: 130, sortable: true },
    { key: 'BillDate', label: 'Date', width: 90, sortable: true },
    { key: 'BillTime', label: 'Time', width: 70 },
    { key: 'Counter', label: 'Counter', width: 80 },
    { key: 'Salesman', label: 'Salesman', width: 90 },
    { key: 'CustomerName', label: 'Customer', width: 110 },
    { key: 'TotalItems', label: 'Items', width: 55, align: 'center' },
    { key: 'TotalQty', label: 'Qty', width: 55, align: 'center' },
    { key: 'GrossAmount', label: 'Gross', width: 90, align: 'right', format: 'currency' },
    { key: 'ItemDiscount', label: 'Item Disc', width: 80, align: 'right', format: 'currency' },
    { key: 'BillDiscount', label: 'Bill Disc', width: 80, align: 'right', format: 'currency' },
    { key: 'TaxAmount', label: 'Tax', width: 70, align: 'right', format: 'currency' },
    { key: 'NetAmount', label: 'Net Amt', width: 100, align: 'right', format: 'currency' },
    { key: 'CashAmount', label: 'Cash', width: 80, align: 'right', format: 'currency' },
    { key: 'CardAmount', label: 'Card', width: 80, align: 'right', format: 'currency' },
    { key: 'Status', label: 'Status', width: 80, align: 'center', render: (val) => (
      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${val === 'Completed' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
        {val}
      </span>
    )}
  ];

  const FiltersSection = () => (
    <div className="flex flex-wrap gap-3 items-end">
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>From Date</label>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={`px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`} />
      </div>
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>To Date</label>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={`px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`} />
      </div>
      <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Bill No</label>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="text" value={billNoSearch} onChange={(e) => setBillNoSearch(e.target.value)} placeholder="Search Bill No..." className={`pl-8 pr-3 py-2 rounded border text-sm w-40 ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`} />
        </div>
      </div>
    </div>
  );

  // Calculate totals for summary
  const totals = data.reduce((acc, row) => ({
    gross: acc.gross + parseFloat(row.GrossAmount || 0),
    net: acc.net + parseFloat(row.NetAmount || 0),
    cash: acc.cash + parseFloat(row.CashAmount || 0),
    card: acc.card + parseFloat(row.CardAmount || 0),
  }), { gross: 0, net: 0, cash: 0, card: 0 });

  const summaryCards = [
    { title: 'Total Gross', value: formatCurrency(totals.gross), color: 'blue' },
    { title: 'Total Net', value: formatCurrency(totals.net), color: 'green' },
    { title: 'Cash Collection', value: formatCurrency(totals.cash), color: 'purple' },
    { title: 'Card Collection', value: formatCurrency(totals.card), color: 'orange' },
  ];

  return (
    <ReportLayout
      title="Bill to Bill Report"
      subtitle="Detailed bill-wise transaction report"
      icon={<FileText size={20} />}
      theme={theme}
      columns={columns}
      data={data}
      loading={loading}
      error={error}
      reportCode="POS_BILL_TO_BILL"
      summaryCards={summaryCards}
      filtersComponent={<FiltersSection />}
      pagination={{ page, pageSize, totalRows, totalPages, onPageChange: setPage, onPageSizeChange: (s) => { setPageSize(s); setPage(1); } }}
      onRefresh={handleRefresh}
      exportFileName={`Bill_To_Bill_${fromDate}_${toDate}`}
    />
  );
};

export default POSBillToBillReport;
